import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useGetUserPasskeys, useDeletePasskey } from "api/usePasskeyQueries";
import { Passkey } from "api/types";
import ConfirmButtonWithDialog from "components/lib/confirmButtonWithDialog";
import { Table } from "components/Table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "components/typography";
import { format } from "date-fns";
import { Icon, Button, useSnackbar } from "@mindlercare/ui-web";

type Args = {
  onDeleteClick: (row: Passkey) => void;
};

const buildPasskeyColumns = (args: Args) => {
  const passkeyColumnHelper = createColumnHelper<Passkey>();
  const columns: ColumnDef<Passkey>[] = [
    passkeyColumnHelper.accessor("id", {
      header: "USER_SETTINGS.PASSKEYS.TABLE_HEADER_PASSKEY_ID",
      cell: (info) => info.getValue(),
    }),
    passkeyColumnHelper.accessor("lastUsed", {
      header: "USER_SETTINGS.PASSKEYS.TABLE_HEADER_LAST_USED_AT",
      cell: (info) => info.getValue(),
    }),
    passkeyColumnHelper.accessor("usage", {
      header: "USER_SETTINGS.PASSKEYS.TABLE_HEADER_USAGE_COUNT",
      cell: (info) => info.getValue(),
    }),
    {
      id: "actions",
      header: "USER_SETTINGS.PASSKEYS.TABLE_HEADER_DELETE_ACTION",
      cell: ({ row }) => (
        <Button variant="icon" onClick={() => args.onDeleteClick(row.original)}>
          <Icon.Trash fill="red" width="16" />
        </Button>
      ),
    },
  ] as Array<ColumnDef<Passkey, unknown>>;
  return columns;
};

export const PasskeySettings = () => {
  const { data: passkeys = [] } = useGetUserPasskeys();
  const formattedPasskeys = passkeys.map((passkey) => ({
    ...passkey,
    lastUsed: format(new Date(passkey.lastUsed), "yyyy-MM-dd HH:mm:ss"),
  }));
  const [deletingPasskey, setDeletingPasskey] = useState<Passkey | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { mutate: deletePasskeyMutate } = useDeletePasskey();
  const columns = buildPasskeyColumns({ onDeleteClick: setDeletingPasskey });
  return (
    <>
      <Typography insensitive variant="h3">
        {t("USER_SETTINGS.PASSKEYS.SETTING_TITLE")}
      </Typography>
      <Typography insensitive variant="body">
        {t("USER_SETTINGS.PASSKEYS.SETTING_DESCRIPTION")}
      </Typography>
      <Table columns={columns} data={formattedPasskeys} />
      <ConfirmButtonWithDialog
        description={t("USER_SETTINGS.PASSKEYS.CONFIRM_DELETE")}
        open={deletingPasskey !== null}
        handleClose={async (confirmed: boolean) => {
          if (!confirmed) {
            setDeletingPasskey(null);
            return;
          }
          if (!deletingPasskey) return;
          deletePasskeyMutate(deletingPasskey.id, {
            onSuccess: () => {
              setDeletingPasskey(null);
              enqueueSnackbar({
                text: t("USER_SETTINGS.PASSKEYS.DELETE_DONE"),
                isError: false,
              });
            },
          });
        }}
        confirmText={t("YES")}
      />
    </>
  );
};
